import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export function KPIWidget({
  title,
  value,
  subtext,
  trend,
  color,
  icon,
}: {
  title: string;
  value: string;
  subtext: string;
  trend: 'up' | 'down';
  color: 'primary' | 'success' | 'warning' | 'error' | 'secondary';
  icon: React.ReactNode;
}) {
  const colorMap = {
    primary: {
      bg: 'bg-[#00758d]/10',
      text: 'text-[#00758d]',
      border: 'border-[#00758d]/20',
      valueClass: 'text-slate-900',
    },
    secondary: {
      bg: 'bg-[#00303c]/10',
      text: 'text-[#00303c]',
      border: 'border-[#00303c]/20',
      valueClass: 'text-slate-900',
    },
    success: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      valueClass: 'text-emerald-950',
    },
    warning: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', valueClass: 'text-amber-950' },
    error: { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200', valueClass: 'text-rose-950' },
  };

  const TrendIcon = trend === 'up' ? ArrowUpRight : ArrowDownRight;
  const trendColor = trend === 'up' ? 'text-emerald-600' : 'text-rose-600';
  const theme = colorMap[color];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200 group relative overflow-hidden">
      <div
        className={cn(
          'absolute top-0 right-0 w-24 h-24 rounded-bl-full bg-gradient-to-bl from-current opacity-[0.03] transition-opacity group-hover:opacity-[0.06]',
          theme.text,
        )}
      />

      <div className="flex justify-between items-start mb-4 relative z-10">
        <h3 className="text-sm font-semibold text-slate-500 max-w-[70%] leading-snug">{title}</h3>
        <div className={cn('p-2 rounded-lg border', theme.bg, theme.text, theme.border)}>
          {React.cloneElement(icon as React.ReactElement<{ className?: string }>, { className: 'w-5 h-5' })}
        </div>
      </div>

      <div className="relative z-10">
        <div className={cn('text-[28px] font-bold tracking-tight mb-1 font-mono', theme.valueClass)}>{value}</div>
        <div className="flex items-center gap-1.5 mt-2">
          <span
            className={cn(
              'inline-flex items-center text-xs font-bold px-1.5 py-0.5 rounded-md bg-slate-50',
              trendColor,
            )}
          >
            <TrendIcon className="w-3 h-3 mr-0.5 stroke-[3]" />
            {trend === 'up' ? '+12%' : '-4%'}
          </span>
          <span className="text-xs text-slate-400 font-medium truncate">{subtext}</span>
        </div>
      </div>
    </div>
  );
}
